import { useMemo } from 'react'
import moment from 'moment'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { EmptyObject } from 'lib/object/objectUtils'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/SearchTileFlightOnlyPricing'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import WalledContent from 'components/Common/WalledContent'
import { calculateOfferTileDiscountPercentage } from 'lib/search/searchUtils'
import { isGuestPublicRateSearchPrice } from 'lib/search/guestPublicRates'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { MALDIVES_BUNDLE_DEFAULT_GUESTS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { ChertoExperiments } from 'constants/experiments'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import CharterBundlePricing from './CharterBundlePricing'
import { getCharterBundleSearchTilePricing } from './charterBundleSearchTilePricing'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import useHotelSearchTileMinStayPricing from './useHotelSearchTileMinStayPricing'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  bestRate?: App.OfferAvailableRate
  duration?: number
  hidePrice?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
  disableCharterPricing?: boolean
  showCompactPricing?: boolean
  compactPriceSize?: 'S' | 'M' | 'L'
  pricingAlign?: 'start' | 'end'
  hideMemberWalls?: boolean
}

function HotelSearchTilePriceStack({
  bestPackage,
  filters,
  offer,
  bestRate,
  duration,
  hidePrice = false,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
  disableCharterPricing = false,
  showCompactPricing = false,
  compactPriceSize = 'L',
  pricingAlign = 'start',
  hideMemberWalls = false,
}: Props) {
  const hasDates = !!(filters?.checkIn && filters?.checkOut && filters?.rooms?.length)
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? EmptyObject, offerId: offer.id })
  const {
    shouldUseAgentHubFlashOfferMinStayPricing,
    minimumDurationPackage,
    shouldUsePublicOfferMinStayPricing,
    publicOfferAlreadyFetched,
    publicOfferMinDurationPackage,
    applyPublicOfferMinStayPricing,
    usesMinStayPricing,
  } = useHotelSearchTileMinStayPricing(offer, filters)

  const pricingPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (shouldUseAgentHubFlashOfferMinStayPricing) {
      return minimumDurationPackage
    }
    if (applyPublicOfferMinStayPricing) {
      return publicOfferMinDurationPackage
    }
    if (shouldUsePublicOfferMinStayPricing && !publicOfferAlreadyFetched) {
      // Show loading indicator rather than flash the search-stream price while
      // the bypass-region offer is in flight.
      return undefined
    }
    return bestPackage
  }, [shouldUseAgentHubFlashOfferMinStayPricing, minimumDurationPackage, applyPublicOfferMinStayPricing, publicOfferMinDurationPackage, shouldUsePublicOfferMinStayPricing, publicOfferAlreadyFetched, bestPackage])
  const oneClickBookingRestorationPackagePricing = isAgentHubEnabled ? undefined : filters?.oneClickBookingRestoration?.packagePricing

  const availableRate = useMemo<App.OfferAvailableRate | undefined>(() => {
    if (oneClickBookingRestorationPackagePricing && pricingPackage) {
      // One-click booking restoration has already validated and priced the exact checkout cart package.
      // OfferTilePricing accepts package-specific live totals through availableRate, so adapt the
      // restoration totals into that shape instead of letting the normal best-rate path replace them.
      const rooms = filters?.rooms ?? []
      const packageSurcharge = pricingPackage.surcharge ?? 0
      const extraGuestSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pricingPackage)
      const price = Math.max(oneClickBookingRestorationPackagePricing.price + oneClickBookingRestorationPackagePricing.surcharge - packageSurcharge - extraGuestSurcharge, 0)
      const memberPriceWithSurcharge = Math.max(oneClickBookingRestorationPackagePricing.memberPrice + oneClickBookingRestorationPackagePricing.surcharge - extraGuestSurcharge, 0)

      return {
        offerId: offer.id,
        price,
        memberPrice: oneClickBookingRestorationPackagePricing.memberPrice,
        memberPriceWithSurcharge,
        surcharge: oneClickBookingRestorationPackagePricing.surcharge,
        hotelPrice: oneClickBookingRestorationPackagePricing.price,
        hotelMemberPrice: oneClickBookingRestorationPackagePricing.memberPrice,
        hotelValue: oneClickBookingRestorationPackagePricing.value,
        hotelMemberValue: oneClickBookingRestorationPackagePricing.memberValue,
        // Flash restoration is hotel-only, so there is no bundled flight component to add.
        flightPrice: 0,
        extraGuestSurcharge: oneClickBookingRestorationPackagePricing.extraGuestSurcharge,
        taxesAndFees: oneClickBookingRestorationPackagePricing.taxesAndFees,
        propertyFees: oneClickBookingRestorationPackagePricing.propertyFees,
        duration: pricingPackage.duration,
        packageId: pricingPackage.id,
        packageUniqueKey: pricingPackage.uniqueKey,
        roomRateId: pricingPackage.roomRate.id,
        // Required by App.OfferAvailableRate, but not read by this search-tile pricing path.
        availableRooms: 1,
        hasTactical: false,
      }
    }

    if (usesMinStayPricing) {
      // These tiles intentionally price from the minimum-stay package, not a live rate.
      return undefined
    }

    return bestRate
  }, [bestRate, filters?.rooms, offer, oneClickBookingRestorationPackagePricing, pricingPackage, usesMinStayPricing])

  const pricingSearchPrices = usesMinStayPricing ? undefined : directSearchPrices
  const couldShowPublicPriceToGuest = isGuestPublicRateSearchPrice(filters, pricingSearchPrices)
  const { isAnyVariant: isLoggedOutPricingVariant } = useExperiment(ChertoExperiments.sppLoggedOutPricing, {
    canActivate: couldShowPublicPriceToGuest,
  })
  const showPublicPriceToGuest = couldShowPublicPriceToGuest && isLoggedOutPricingVariant
  const pricingFilters = useMemo(() => {
    if (!usesMinStayPricing || !filters || !pricingPackage?.roomRate?.id || !pricingPackage.roomType?.id) {
      return filters
    }

    return {
      ...filters,
      roomRateId: pricingPackage.roomRate.id,
      roomTypeId: pricingPackage.roomType.id,
    }
  }, [filters, pricingPackage, usesMinStayPricing])
  const discountPercentage = useMemo(() => {
    if (offer.isDiscountPillHidden) {
      return undefined
    }

    if (pricingSearchPrices?.lowestPrice && pricingSearchPrices?.lowestPriceValue) {
      return calculateOfferTileDiscountPercentage({
        price: pricingSearchPrices.lowestPrice,
        value: pricingSearchPrices.lowestPriceValue,
      })
    }

    if (oneClickBookingRestorationPackagePricing) {
      return calculateOfferTileDiscountPercentage({
        price: oneClickBookingRestorationPackagePricing.price + oneClickBookingRestorationPackagePricing.surcharge,
        value: oneClickBookingRestorationPackagePricing.value,
      })
    }

    if (pricingPackage) {
      return calculateOfferTileDiscountPercentage({
        price: pricingPackage.price,
        value: pricingPackage.value,
      })
    }

    return undefined
  }, [offer, oneClickBookingRestorationPackagePricing, pricingPackage, pricingSearchPrices])

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { isPricePerPerson, duration: contextDuration } = useMaldivesDirectBundleContext()

  const isCharterBundleOffer = isFlashHotelOffer(offer) &&
    offer.offerFlightsEnabled &&
    offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const charterSearchPrices = offerMetaData?.pricing
  const charterPricing = getCharterBundleSearchTilePricing({
    charterSearchPrices,
  })

  if (!disableCharterPricing && isCharterBundleOffer && isDisplayablePrice(charterPricing?.price)) {
    const totalGuests = filters?.rooms ? sumUpOccupancies(filters.rooms) : MALDIVES_BUNDLE_DEFAULT_GUESTS
    return <Group direction="vertical" gap={4} horizontalAlign={pricingAlign}>
      <LuxPlusOfferVoucherCallout
        offer={offer}
        mode="search"
        align="start"
      />
      <CharterBundlePricing
        offer={offer}
        pricing={{
          price: charterPricing.price,
          memberPrice: charterPricing.memberPrice,
          value: charterPricing.value,
          discountPercent: charterPricing.discountPercent,
          memberDiscountPercent: charterPricing.memberDiscountPercent,
          totalGuests,
          roomCount: filters?.rooms?.length,
        }}
        isPricePerPerson={isPricePerPerson}
        nights={contextDuration ?? charterSearchPrices?.duration ?? duration}
        filters={filters}
        hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
        hideReturnFareFree
      />
    </Group>
  }

  const hasPricing = hasDisplayablePackagePrice(pricingPackage) ||
    isDisplayablePrice(availableRate?.price) ||
    isDisplayablePrice(pricingSearchPrices?.lowestPrice)

  if (!pricingPackage) {
    return <LoadingIndicator inline ballSize={12} />
  }

  if (!hasPricing) return null

  // Additional terms match derivation of `<WalledContent/>`'s `enforced` prop in
  // `<HotelSearchTileAction/>`.
  const showPublicRateWallMessage = showPublicPriceToGuest && !hideMemberWalls && offer.walledGarden

  return <div>
    <WalledContent
      offerType={offer.type}
      enforced={!hideMemberWalls && offer.walledGarden && !showPublicPriceToGuest}
      wall={<SearchTileWalledMessage discountPercentage={discountPercentage} />}
    >
      <Group direction="vertical" gap={8} fullWidth>
        {!offer.bundledWithFlightsOnly && <OfferTilePricing
          isSearchTile
          offer={offer}
          hasDates={hasDates}
          checkInDate={checkInDate}
          pkg={pricingPackage}
          availableRate={availableRate}
          align={pricingAlign}
          filters={pricingFilters}
          duration={usesMinStayPricing ? pricingPackage.duration : duration}
          hidePrice={hidePrice}
          overrideRoomsNumber={offerMetaData?.rooms?.length}
          isPricePerNight={showPerNightPricing}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
          compactExperimentLayout={showCompactPricing}
          compactPriceSize={compactPriceSize}
        />}
        {(offer.bundledWithFlightsOnly && !hidePrice) && (
          <SearchTileFlightOnlyPricing
            offer={offer}
            rooms={filters?.rooms}
            hasDates={hasDates}
            pkg={pricingPackage}
            pricing={bestRate}
            filters={filters}
          />
        )}
        {showPublicRateWallMessage && <SearchTileWalledMessage disablePricingCalculatedEvent />}
      </Group>
    </WalledContent>
  </div>
}

export default HotelSearchTilePriceStack
